import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { requests } from "../../../common/RestAPI";
import { base64Encoding, getTimestampToDate } from "../../../common/util";
import { SESSIONSTORAGE, FONT } from "../../../value/staticValues";
import { PAGE_URL, PLATFORM_URL } from "../../../value/Url";
import { ALERTS, ERROR_CODE, POPUP_TYPE } from "../../../value/type";

import { ValidationError } from "../../../common/ExceptionError";

import { DetailProps } from "../../../interfaceTypes/ContentsProps";
import { useTranslation } from "react-i18next";
import PodProFileDetail from "./PodProfileDetail";
import { PodProfileDetailData, initPodProfileDetailData, podProfileAddContainerModalContent, podProfileAddVolumeModalContent, podProfileUpdateContainerModalContent, podProfileUpdateModalContent, podProfileUpdateVolumeModalContent, PodProfileContainerData } from "./PodProfileData";



export interface PodProfileContainer {
    podProfileContainerId: string;
    podProfileContainerName: string;
    serviceTypeCode: string;
    requestCpuCount: number;
    requestMemoryCapacityMb: number;
    limitsCpuCount: number;
    limitsMemoryCapacityMb: number;
    gpuYn: boolean;
    commands: string;
    volumeMounts: string;
    envs: string;
    ports: string;
    livenessProbeYn: boolean;
    livenessProbeCommands: string;
    livenessProbeHttpGetPath: string;
    livenessProbeHttpGetPort: number;
    livenessProbeHttpGetHeaders: string;
    livenessProbeTcpSocketPort: number;
    livenessProbeInitialDelaySec: number;
    livenessProbePeriodSec: number;
    livenessProbeTimeoutSec: number;
    readnessProbeYn: boolean;
    readnessProbeCommands: string;
    readnessProbeHttpGetPath: string;
    readnessProbeHttpGetPort: number;
    readnessProbeHttpGetHeaders: string;
    readnessProbeTcpSocketPort: number;
    readnessProbeInitialDelaySec: number;
    readnessProbePeriodSec: number;
    readnessProbeTimeoutSec: number;
    containerImageName: string;
    createDate: string;
    updateDate: string;
};

export interface PodProfileVolume {
    podProfileVolumeId: string;
    podProfileVolumeName: string;
    podProfileVolumeTypeCode: string;
    hostPathTypeCode: string;
    claimName: string;
    hostPath: string;
    createDate: string;
    updateDate: string;
};


export default function PodProfileDetailContainter(props:DetailProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [infoText, setInfoText] = useState('');
    const [infoError, setInfoError] = useState(false);

    const [targetId, setTargetId] = useState(props.targetId || '');

    const [podProfileName, setPodProfileName] = useState("");
    const [podProfileDesc, setPodProfileDesc] = useState("");
    // const [podProfileYaml, setPodProfileYaml] = useState("");
    const [deploymentYn, setDeploymentYn] = useState(false);
    const [deploymentCount, setDeploymentCount] = useState(0);
    const [labels, setLabels] = useState("");
    const [annotations, setAnnotations] = useState("");
    const [selector, setSelector] = useState("");
    const [templateLabels, setTemplateLabels] = useState("");

    const [podProfileDetailData, setPodProfileDetailData] = useState<PodProfileDetailData>(initPodProfileDetailData)


    const [refreshYn, setRefreshYn] = useState(false);

    const [executeDeletePodProfileContainerId, setExecuteDeletePodProfileContainerId] = useState("");
    const [executeDeletePodProfileVolumeId, setExecuteDeletePodProfileVolumeId] = useState("");


    useEffect(() => {
        props.setLoading(false);
        AlertFunction('');
    }, []);

    function AlertFunction(text:string, error=false) {
        setInfoText(text);
        setInfoError(error);
    }

    useEffect(() => {
        if(!targetId) {
            const localTargetId = sessionStorage.getItem(SESSIONSTORAGE.POD_PROFILE_TARGET_ID);
            if(localTargetId) {
                setTargetId(localTargetId);
            } else {
                navigate(`${PAGE_URL.ERRORPAGE}`);
            }
        } else {
            sessionStorage.setItem(SESSIONSTORAGE.POD_PROFILE_TARGET_ID, targetId);
        }
    }, [props]);

    useEffect(() => {
        if(targetId) {
            executeLoadPodProfile();
        }
    }, [targetId]);

    useEffect(() => {
        if(targetId && refreshYn) {
            executeLoadPodProfile();
        }
    }, [refreshYn]);

    useEffect(() => {
        if(executeDeletePodProfileContainerId) {
            executeDeletePodProfileContainer();
        } else if(executeDeletePodProfileVolumeId) {
            executeDeletePodProfileVolume();
        }

    }, [executeDeletePodProfileContainerId, executeDeletePodProfileVolumeId]);


    function executeLoadPodProfile() {
        props.setLoading(true);

        requests.get(PLATFORM_URL.POD_PROFILE + "/" + targetId)
        .then((response) => {
            setPodProfileDetailData(response.data);
            props.setLoading(false);
            setRefreshYn(false);
        })
        .catch((error) => {
            props.setLoading(false);
            AlertFunction(t("MESSAGE_CANNOT_LOAD_DATA_MESSAGE"), true);
            setRefreshYn(false);
        });
    }

    function executeDeletePodProfileContainer() {
        const deletePackageData = {
            pod_profile_containers: [
                {
                    remove_yn: true,
                    pod_profile_container_id: executeDeletePodProfileContainerId
                }
            ]
        }
        props.setLoading(true);
        requests.put(PLATFORM_URL.POD_PROFILE + "/" + targetId, deletePackageData)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeletePodProfileContainerId("");
            deletePodProfileContainerResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeletePodProfileContainerId("");
            deletePodProfileContainerResult({successYn: false});
        })
    }

    function executeDeletePodProfileVolume() {
        const deletePackageData = {
            pod_profile_volumes: [
                {
                    remove_yn: true,
                    pod_profile_volume_id: executeDeletePodProfileVolumeId
                }
            ]
        }
        props.setLoading(true);
        requests.put(PLATFORM_URL.POD_PROFILE + "/" + targetId, deletePackageData)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeletePodProfileVolumeId("");
            deletePodProfileVolumeResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeletePodProfileVolumeId("");
            deletePodProfileVolumeResult({successYn: false});
        })
    }

    function updatePodProfileResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }
    function addPodProfileContainerResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_ADD_CONTAINER_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function deletePodProfileContainerResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_DELETE_CONTAINER_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function updatePodProfileContainerResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_UPDATE_CONTAINER_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }
    function addPodProfileVolumeResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_ADD_VOLUME_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function updatePodProfileVolumeResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_UPDATE_VOLUME_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }
    function deletePodProfileVolumeResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_POD_PROFILE_DELETE_VOLUME_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }


    function clickUpdate() {
        const data = {
            targetId: podProfileDetailData.pod_profile_id,
            podProfileName: podProfileDetailData.pod_profile_name,
            podProfileDesc: podProfileDetailData.pod_profile_description,
            deploymentYn: podProfileDetailData.deployment_yn,
            deploymentCount: podProfileDetailData.deployment_pod_count,
            labels: podProfileDetailData.labels,
            annotations: podProfileDetailData.annotations,
            selector: podProfileDetailData.selector,
            templateLabels: podProfileDetailData.template_labels
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...podProfileUpdateModalContent, openYn: true, resultCb: updatePodProfileResult, args: data});
        }
    }

    function clickAddContainer() {
        const data = {
            podProfileId: targetId,
            podProfileData: podProfileDetailData
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...podProfileAddContainerModalContent, openYn: true, resultCb: addPodProfileContainerResult, args: data});
        }
    }

    function clickUpdateContainer(item:PodProfileContainer) {
        const data = item;
        data['podProfileId'] = targetId;
        data['projectId'] = podProfileDetailData.project.project_id;
        data['podProfileName'] = podProfileDetailData.pod_profile_name;
        data['podProfileDesc'] = podProfileDetailData.pod_profile_description;
        data['deploymentYn'] = podProfileDetailData.deployment_yn;
        data['deploymentCount'] = podProfileDetailData.deployment_pod_count;
        data['labels'] = podProfileDetailData.labels;
        data['annotations'] = podProfileDetailData.annotations;
        data['selector'] = podProfileDetailData.selector;
        data['templateLabels'] = podProfileDetailData.template_labels;
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...podProfileUpdateContainerModalContent, openYn: true, resultCb: updatePodProfileContainerResult, args: data});
        }
    }

    function clickDeleteContainer(item:PodProfileContainerData) {
        props.showPopupModalCb({openYn: true,
            type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
            title: t("MESSAGE_POD_PROFILE_CONTAINER_DELETE_REQUEST_TITLE_MESSAGE"),
            message: t("MESSAGE_POD_PROFILE_CONTAINER_DELETE_REQUEST_MESSAGE"),
            code: "",
            cancelButtonYn: true,
            confirmButtonCb: (code) => {setExecuteDeletePodProfileContainerId(item.pod_profile_container_id)},
        });
    }

    function clickAddVolume() {
        const data = {
            podProfileId: targetId,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...podProfileAddVolumeModalContent, openYn: true, resultCb: addPodProfileVolumeResult, args:data});
        }
    }
    function clickUpdateVolume(item){
        const data = item;
        data['podProfileId'] = targetId;
        data['projectId'] = podProfileDetailData.project.project_id;
        data['podProfileName'] = podProfileDetailData.pod_profile_name;
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...podProfileUpdateVolumeModalContent, openYn: true, resultCb: updatePodProfileVolumeResult, args: data});
        }
    }

    function clickDeleteVolume(item) {
        props.showPopupModalCb({openYn: true,
            type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
            title: t("MESSAGE_POD_PROFILE_VOLUME_DELETE_REQUEST_TITLE_MESSAGE"),
            message: t("MESSAGE_POD_PROFILE_VOLUME_DELETE_REQUEST_MESSAGE"),
            code: "",
            cancelButtonYn: true,
            confirmButtonCb: (code) => {setExecuteDeletePodProfileVolumeId(item.pod_profile_volume_id)},
        });
    }
    return (
        <PodProFileDetail
            detailData={podProfileDetailData}
            infoText={infoText}
            infoError={infoError}
            updateFunction={clickUpdate}
            addContainerFunction={clickAddContainer}
            updateContainerFunction={clickUpdateContainer}
            deleteContainerFunction={clickDeleteContainer}
            addVolumeFunction={clickAddVolume}
            updateVolumeFunction={clickUpdateVolume}
            deleteVolumeFunction={clickDeleteVolume}
        />
    );
}
